import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Grid,
  Typography,
  Paper,
  alpha,
  Skeleton,
  LinearProgress,
  Avatar,
  Fade,
  Grow,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AlbumIcon from '@mui/icons-material/Album';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CategoryIcon from '@mui/icons-material/Category';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getDashboardStatsAdmin } from '../Common/actions';
import { API_URL } from '../../constants';
import { FullPageLoader } from '../../components/Loader';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: 'calc(100vh - 100px)',
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: alpha('#1E293B', 0.5),
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha('#fff', 0.08)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`,
  position: 'relative',
  overflow: 'hidden',
}));

const StatCard = styled(Paper)(({ theme, gradient }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: gradient || '#fff',
  border: 'none',
  boxShadow: `0 8px 32px ${alpha('#000', 0.12)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const StatIconBox = styled(Box)({
  width: 52,
  height: 52,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  marginBottom: 20,
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: 26,
    color: '#fff',
  },
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: '#F1F5F9',
  marginBottom: theme.spacing(2.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    color: '#12B4A1',
    fontSize: 22,
  },
}));

const StatusRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
  padding: '12px 0',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const QuickStatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: alpha('#0F172A', 0.5),
  border: `1px solid ${alpha('#fff', 0.06)}`,
  marginBottom: theme.spacing(1.5),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ReleaseCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  background: alpha('#1E293B', 0.6),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha('#fff', 0.06)}`,
  boxShadow: `0 4px 20px ${alpha('#000', 0.25)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 16px 40px ${alpha('#12B4A1', 0.2)}`,
    borderColor: alpha('#12B4A1', 0.3),
    background: alpha('#1E293B', 0.8),
    '& .album-artwork': {
      transform: 'scale(1.05)',
    },
  },
}));

const AlbumArtwork = styled(Avatar)(({ theme }) => ({
  width: '100%',
  height: 140,
  borderRadius: 12,
  marginBottom: 12,
  backgroundColor: alpha('#12B4A1', 0.08),
  transition: 'transform 0.3s ease',
  position: 'relative',
}));

const StatusBadge = styled(Chip)(({ status }) => {
  const colors = {
    approved: { bg: alpha('#66BB6A', 0.12), color: '#2E7D32', border: alpha('#66BB6A', 0.3) },
    rejected: { bg: alpha('#FF6B6B', 0.12), color: '#C62828', border: alpha('#FF6B6B', 0.3) },
    in_review: { bg: alpha('#FFA726', 0.12), color: '#E65100', border: alpha('#FFA726', 0.3) },
    takendown: { bg: alpha('#9E9E9E', 0.12), color: '#616161', border: alpha('#9E9E9E', 0.3) },
    draft: { bg: alpha('#2196F3', 0.12), color: '#1565C0', border: alpha('#2196F3', 0.3) },
  };
  const statusColor = colors[status] || colors.draft;
  return {
    backgroundColor: statusColor.bg,
    color: statusColor.color,
    border: `1px solid ${statusColor.border}`,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: 24,
    '& .MuiChip-label': {
      padding: '0 10px',
    },
  };
});

const ProgressBar = styled(LinearProgress)(({ barcolor }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: alpha('#fff', 0.08),
  '& .MuiLinearProgress-bar': {
    backgroundColor: barcolor || '#12B4A1',
    borderRadius: 3,
  },
}));

const getStatusLabel = (status) => {
  const labels = {
    approved: 'Approved',
    rejected: 'Rejected',
    in_review: 'In Review',
    takendown: 'Takendown',
    draft: 'Draft',
  };
  return labels[status] || status;
};

export default function Dashboard() {
  const history = useHistory();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStatsAdmin()
      .then((res) => {
        setStats(res.body || {});
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const statCards = [
    {
      title: 'Total Releases',
      value: stats?.total_releases || 0,
      icon: <AlbumIcon />,
      gradient: 'linear-gradient(135deg, #1E3A5F 0%, #2D5478 100%)',
      path: '/releases/review',
      description: 'All releases',
    },
    {
      title: 'Approved',
      value: stats?.approved_releases || 0,
      icon: <CheckCircleIcon />,
      gradient: 'linear-gradient(135deg, #0D4F4A 0%, #12B4A1 100%)',
      path: '/release/approved',
      description: 'Live on platforms',
    },
    {
      title: 'In Review',
      value: stats?.status_distribution?.in_review || 0,
      icon: <NewReleasesIcon />,
      gradient: 'linear-gradient(135deg, #4A3728 0%, #8B6914 100%)',
      path: '/releases/review',
      description: 'Pending approval',
    },
    {
      title: 'Total Tracks',
      value: stats?.total_tracks || 0,
      icon: <MusicNoteIcon />,
      gradient: 'linear-gradient(135deg, #2D2A4A 0%, #4A4478 100%)',
      description: 'Audio files',
    },
  ];

  const statusDistribution = stats?.status_distribution || {};
  const totalForPercentage = stats?.total_releases || 1;

  const statusItems = [
    { key: 'approved', label: 'Approved', color: '#66BB6A', icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, path: '/release/approved' },
    { key: 'rejected', label: 'Rejected', color: '#FF6B6B', icon: <CancelIcon sx={{ fontSize: 18 }} />, path: '/release/rejected' },
    { key: 'in_review', label: 'In Review', color: '#FFA726', icon: <NewReleasesIcon sx={{ fontSize: 18 }} />, path: '/releases/review' },
    { key: 'takendown', label: 'Takendown', color: '#9E9E9E', icon: <RemoveCircleIcon sx={{ fontSize: 18 }} />, path: '/release/takendown' },
    { key: 'draft', label: 'Draft', color: '#2196F3', icon: <EditNoteIcon sx={{ fontSize: 18 }} /> },
  ];

  const quickStats = [
    { label: 'Primary Artists', value: stats?.primary_artists || 0, icon: <PeopleIcon />, color: '#12B4A1' },
    { label: 'Featuring Artists', value: stats?.featuring_artists || 0, icon: <PeopleIcon />, color: '#0596B3' },
    { label: 'Total Tracks', value: stats?.total_tracks || 0, icon: <MusicNoteIcon />, color: '#9C27B0' },
    { label: 'Genres', value: stats?.top_genres?.length || 0, icon: <AssignmentIcon />, color: '#FF6B6B' },
  ];

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const timestamp = Number(dateValue);
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Dashboard - Fronic Media Labels</title>
      </Helmet>
      <FullPageLoader show={loading} message="Loading dashboard..." />

      {/* Stats Overview */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Grow in={!loading} timeout={300 + index * 100}>
              <div>
                {loading ? (
                  <Skeleton variant="rounded" height={180} sx={{ borderRadius: 4 }} />
                ) : (
                  <StatCard
                    elevation={0}
                    gradient={stat.gradient}
                  >
                    <StatIconBox className="stat-icon">
                      {stat.icon}
                    </StatIconBox>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, mb: 0.5, fontSize: '2.2rem' }}
                    >
                      {stat.value.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, opacity: 0.95, mb: 0.25 }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75, fontSize: '0.8rem' }}>
                      {stat.description}
                    </Typography>
                  </StatCard>
                )}
              </div>
            </Grow>
          </Grid>
        ))}
      </Grid>

      {/* Top 5 Sub Labels & Top Genres - Side by Side */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top 5 Sub Labels with Donut Chart */}
        <Grid item xs={12} md={6}>
          <Fade in={!loading} timeout={500}>
            <GlassCard elevation={0}>
              <SectionTitle>
                <EmojiEventsIcon />
                Top 5 Sub Labels (Approved Releases)
              </SectionTitle>
              {loading ? (
                <Skeleton variant="rounded" height={240} />
              ) : stats?.top_sub_labels?.length > 0 ? (
                <Box>
                  {/* Pie Chart using Recharts */}
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={stats.top_sub_labels.map((label, index) => ({
                          name: label.name || 'Unknown',
                          value: label.approved_count || 0,
                          color: ['#FFD700', '#C0C0C0', '#CD7F32', '#12B4A1', '#0596B3'][index] || '#64748B',
                        }))}
                        cx="40%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        cornerRadius={6}
                        dataKey="value"
                      >
                        {stats.top_sub_labels.map((label, index) => {
                          const colors = ['#FFD700', '#C0C0C0', '#CD7F32', '#12B4A1', '#0596B3'];
                          return <Cell key={`cell-${index}`} fill={colors[index] || '#64748B'} />;
                        })}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: alpha('#1E293B', 0.95),
                          border: `1px solid ${alpha('#fff', 0.1)}`,
                          borderRadius: '8px',
                          color: '#F1F5F9',
                          fontSize: '0.85rem',
                        }}
                        itemStyle={{ color: '#F1F5F9' }}
                      />
                      <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        iconType="circle"
                        wrapperStyle={{
                          paddingLeft: '20px',
                          lineHeight: '2',
                        }}
                        formatter={(value, entry) => (
                          <span style={{ color: '#E2E8F0', fontSize: '0.8rem', marginLeft: '5px' }}>
                            {value}: {entry.payload.value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  No sub labels data available
                </Typography>
              )}
            </GlassCard>
          </Fade>
        </Grid>

        {/* Top Genres with Bar Chart */}
        <Grid item xs={12} md={6}>
          <Fade in={!loading} timeout={550}>
            <GlassCard elevation={0}>
              <SectionTitle>
                <CategoryIcon />
                Top Genres
              </SectionTitle>
              {loading ? (
                <Skeleton variant="rounded" height={240} />
              ) : stats?.top_genres?.length > 0 ? (
                <Box>
                  {/* Bar Chart */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 220, mb: 0.5 }}>
                    {stats.top_genres.slice(0, 5).map((genre, index) => {
                      const maxCount = stats.top_genres[0]?.count || 1;
                      const percentage = (genre.count / maxCount) * 100;
                      const barHeight = Math.max((percentage / 100) * 210, 30);
                      const colors = ['#12B4A1', '#0596B3', '#9C27B0', '#FFA726', '#FF6B6B'];
                      const color = colors[index % colors.length];
                      return (
                        <Box
                          key={index}
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: '100%',
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: color, fontSize: '0.75rem', mb: 0.5 }}>
                            {genre.count}
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: 50,
                              height: barHeight,
                              background: `linear-gradient(180deg, ${color} 0%, ${alpha(color, 0.5)} 100%)`,
                              borderRadius: '8px 8px 0 0',
                              transition: 'height 0.5s ease',
                              boxShadow: `0 0 15px ${alpha(color, 0.3)}`,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                  {/* Labels */}
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                    {stats.top_genres.slice(0, 5).map((genre, index) => (
                      <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: '#94A3B8',
                            fontSize: '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {genre.genre}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  No genres data available
                </Typography>
              )}
            </GlassCard>
          </Fade>
        </Grid>
      </Grid>

      {/* Recent Releases */}
      <Fade in={!loading} timeout={600}>
        <Box sx={{ mb: 4 }}>
          <SectionTitle sx={{ mb: 3 }}>
            <AccessTimeIcon />
            Recent Releases
          </SectionTitle>
          {loading ? (
            <Grid container spacing={2}>
              {[...Array(6)].map((_, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Skeleton variant="rounded" height={240} sx={{ borderRadius: 4 }} />
                </Grid>
              ))}
            </Grid>
          ) : stats?.recent_releases?.length > 0 ? (
            <Grid container spacing={2}>
              {stats.recent_releases.map((release, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Grow in={true} timeout={400 + index * 100}>
                    <ReleaseCard
                      elevation={0}
                      onClick={() => history.push(`/view_releases/${release.release_id}`)}
                    >
                      <AlbumArtwork
                        className="album-artwork"
                        variant="rounded"
                        src={release.album_artwork ? (
                          release.album_artwork.startsWith('http')
                            ? release.album_artwork
                            : `${API_URL}/${release.album_artwork.replace(/^\//, '')}`
                        ) : ''}
                      >
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          textAlign: 'center',
                          px: 1
                        }}>
                          <AlbumIcon sx={{ fontSize: 40, color: alpha('#12B4A1', 0.4) }} />
                          <Typography sx={{
                            fontSize: '0.7rem',
                            color: alpha('#94A3B8', 0.7),
                            lineHeight: 1.2
                          }}>
                            Image not available
                          </Typography>
                        </Box>
                      </AlbumArtwork>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: '#F1F5F9',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.25,
                        }}
                      >
                        {release.release_title || 'Untitled'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94A3B8',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 1,
                          fontSize: '0.75rem',
                        }}
                      >
                        {release.primary_artist || 'Unknown Artist'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem', display: 'block', mb: 1 }}>
                        {formatDate(release.release_date)}
                      </Typography>
                      <StatusBadge
                        label={getStatusLabel(release.status)}
                        status={release.status}
                        size="small"
                      />
                    </ReleaseCard>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          ) : (
            <GlassCard elevation={0}>
              <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center', py: 4 }}>
                No recent releases
              </Typography>
            </GlassCard>
          )}
        </Box>
      </Fade>

      {/* Status Distribution & Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Fade in={!loading} timeout={700}>
            <GlassCard elevation={0}>
              <SectionTitle>
                <TrendingUpIcon />
                Release Status Distribution
              </SectionTitle>
              {loading ? (
                <Skeleton variant="rounded" height={220} />
              ) : (
                <Box>
                  {statusItems.map((item) => {
                    const count = statusDistribution[item.key] || 0;
                    const percentage = totalForPercentage > 0 ? (count / totalForPercentage) * 100 : 0;
                    return (
                      <StatusRow key={item.key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 130 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: alpha(item.color, 0.15),
                              color: item.color,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#E2E8F0' }}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, mx: 2 }}>
                          <ProgressBar
                            variant="determinate"
                            value={percentage}
                            barcolor={item.color}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#F1F5F9' }}>
                            {count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
                            {percentage.toFixed(0)}%
                          </Typography>
                        </Box>
                      </StatusRow>
                    );
                  })}
                </Box>
              )}
            </GlassCard>
          </Fade>
        </Grid>

        <Grid item xs={12} md={5}>
          <Fade in={!loading} timeout={800}>
            <GlassCard elevation={0} sx={{ height: '100%' }}>
              <SectionTitle>
                <AssignmentIcon />
                Quick Stats
              </SectionTitle>
              {loading ? (
                <Skeleton variant="rounded" height={220} />
              ) : (
                <Box>
                  {quickStats.map((item, index) => (
                    <QuickStatItem key={index}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(item.color, 0.15)} 0%, ${alpha(item.color, 0.05)} 100%)`,
                          color: item.color,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#F1F5F9' }}>
                          {item.value.toLocaleString()}
                        </Typography>
                      </Box>
                    </QuickStatItem>
                  ))}
                </Box>
              )}
            </GlassCard>
          </Fade>
        </Grid>
      </Grid>

    </PageContainer>
  );
}
